import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { CreditCardService } from '@/domain/card';
import { CardPinService } from '@/domain/card/CardPinService';
import { getCardPin } from '@/react-query/services/pin';
import { getDebitCardPin } from '@/react-query/services/pin/get-debit-card-pin';

import { useCanCardUseNewPinService } from '@domain/card/hooks/useCardCanUseNewPin';
import { Card, CreditCard } from '@generated-types';

interface Options {
    enabled: boolean;
}

export const useCardPin = (card: Card | CreditCard, { enabled }: Options) => {
    const canUseNewPin = useCanCardUseNewPinService(card);

    const fetchFunction = useMemo(() => {
        const isV4 = CreditCardService.isCreditCard(card);
        if (isV4) {
            return () => CardPinService.getCreditCardPin(card.cardNumber);
        }
        if (canUseNewPin) {
            return () => getDebitCardPin(card.id);
        }
        return () => getCardPin(card.type, card.id, card.number);
    }, [card, canUseNewPin]);

    return useQuery({
        queryKey: ['cardPin', card],
        queryFn: fetchFunction,
        enabled,
        staleTime: 60 * 1000,
    });
};
